/**
 * Contributors Hooks
 * React Query hooks for contributor data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contributorsAPI } from '../services/api';

/**
 * Hook to fetch contributors list with filtering
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by status
 * @param {string} filters.pending_approval - 'true' to show pending approval only
 * @param {string} filters.search - Search term
 * @param {number} filters.limit - Page size
 * @param {number} filters.offset - Pagination offset
 */
export const useContributors = (filters = {}) => {
  return useQuery({
    queryKey: ['contributors', filters],
    queryFn: () => contributorsAPI.getAll(filters),
    keepPreviousData: true
  });
};

/**
 * Hook to fetch contributor statistics
 */
export const useContributorStats = () => {
  return useQuery({
    queryKey: ['contributors', 'stats'],
    queryFn: () => contributorsAPI.getStats(),
    staleTime: 30 * 1000 // 30 seconds
  });
};

/**
 * Hook to fetch single contributor with full details
 * @param {number} id - Contributor ID
 */
export const useContributor = (id) => {
  return useQuery({
    queryKey: ['contributors', id],
    queryFn: () => contributorsAPI.getById(id),
    enabled: !!id
  });
};

/**
 * Hook to update contributor settings
 */
export const useUpdateContributor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => contributorsAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(['contributors']);
      queryClient.invalidateQueries(['contributors', id]);
    }
  });
};

/**
 * Hook to approve contributor for public listing
 */
export const useApprovePublicListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => contributorsAPI.approvePublic(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['contributors']);
    }
  });
};

/**
 * Hook to reject contributor for public listing
 */
export const useRejectPublicListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => contributorsAPI.rejectPublic(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(['contributors']);
    }
  });
};
